import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import LandingNav from '../landing/LandingNav';
import LandingFinalCta from '../landing/LandingFinalCta';
import LandingFooter from '../landing/LandingFooter';
import '../landing/landing.css';

export interface LegalSection {
  heading: string;
  paragraphs: string[];
}

interface LegalPageProps {
  title: string;
  updated: string;
  intro: string;
  sections?: LegalSection[];
  /** Rich content rendered inside `.legal-sections` in place of `sections`. */
  children?: ReactNode;
}

/**
 * Shared shell for static legal pages (Privacy, Terms). Wraps the content in the
 * landing's nav + footer so the document feels like part of the site. Styled with
 * the scoped `.advi-landing` / `.legal-*` classes (not Tailwind utilities — the
 * landing reset would otherwise collapse Tailwind spacing). Content is template
 * boilerplate — review with counsel before relying on it.
 */
export default function LegalPage({ title, updated, intro, sections, children }: LegalPageProps) {
  return (
    <div className="advi-landing">
      <LandingNav />

      <div className="legal-doc">
        <Link to="/" className="legal-back">← Back to home</Link>
        <h1 className="legal-title">{title}</h1>
        <p className="legal-updated">Last updated: {updated}</p>
        <p className="legal-intro">{intro}</p>

        <div className="legal-sections">
          {children ??
            sections?.map((s, i) => (
              <section className="legal-section" key={s.heading}>
                <h2>{i + 1}. {s.heading}</h2>
                {s.paragraphs.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </section>
            ))}
        </div>

        <p className="legal-contact">
          Questions about this document? Contact us at{' '}
          <a href="mailto:hello@vib3.app">hello@vib3.app</a>.
        </p>
      </div>

      <LandingFinalCta />
      <LandingFooter />
    </div>
  );
}
